import {
  CSSProperties,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

type UseStuckArgs = {
  topSpace?: number;
  bottomSpace?: number;
  offset?: number;
  isFixedContainer?: boolean;
  linkedComponentId?: string;
};

export const useStuck = ({
  topSpace = 0,
  bottomSpace = 0,
  offset = 0,
  isFixedContainer = false,
  linkedComponentId,
}: UseStuckArgs = {}): [
  MutableRefObject<HTMLDivElement | null>,
  boolean,
  number
] => {
  const stickyContainerRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setStuck] = useState(false);
  const [offsetLeft, setOffsetLeft] = useState(0);

  // Measure offsetLeft only once, when the ref is set
  useEffect(() => {
    const stickyContainer = stickyContainerRef.current;
    if (stickyContainer) {
      const stickyContainerBox = stickyContainer.getBoundingClientRect();
      setOffsetLeft(stickyContainerBox.left);
    }
    // Only run when the ref changes
  }, [stickyContainerRef]);

  useEffect(() => {
    const onScroll = () => {
      const stickyContainer = stickyContainerRef?.current;

      if (stickyContainer && isFixedContainer && linkedComponentId) {
        const linkedComponent = document.getElementById(linkedComponentId);
        if (!linkedComponent) return;
        const linkedComponentDiv = linkedComponent.getBoundingClientRect();
        setStuck(linkedComponentDiv.top - offset <= 0);
      }

      if (stickyContainer && !isFixedContainer) {
        const stickyContainerBox = stickyContainer.getBoundingClientRect();
        const shouldBeSticky = stickyContainerBox.top <= topSpace;
        const isOutOfContainer =
          stickyContainerBox.top + stickyContainerBox.height <= bottomSpace;
        setStuck(shouldBeSticky && !isOutOfContainer);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [
    stickyContainerRef,
    topSpace,
    bottomSpace,
    offset,
    isFixedContainer,
    linkedComponentId,
  ]);

  return [stickyContainerRef, isStuck, offsetLeft];
};

export const getStickyGridStyle = (
  stickySettings: any,
  isStickyGridEnabled: boolean,
  isStuck: boolean,
  centeredInParent: boolean,
  isBuilderPage: boolean = false
) => {
  if (!isStickyGridEnabled) return undefined;
  const style =
    stickySettings?.position === 'sticky'
      ? {
          position: 'sticky' as CSSProperties['position'],
          top: `${stickySettings?.margin || 96}px`,
          left: !centeredInParent || isStuck ? 0 : undefined,
          zIndex: 2,
          // Case centeredInParent is true and not Stuck
          paddingX: centeredInParent && !isStuck ? '4rem' : undefined,
          marginLeft: centeredInParent && !isStuck ? 'auto' : undefined,
          marginRight: centeredInParent && !isStuck ? 'auto' : undefined,
          maxWidth: centeredInParent && !isStuck ? '1280px' : undefined,
        }
      : {
          position: 'fixed' as CSSProperties['position'],
          top:
            stickySettings?.location !== 'bottom'
              ? `${stickySettings?.margin || 0}px`
              : undefined,
          bottom:
            stickySettings?.location === 'bottom'
              ? `${stickySettings?.margin || 0}px`
              : undefined,
          left: 0,
          right: 0,
          zIndex: 102,
          height: isStuck || !stickySettings?.linkedComponentId ? 'auto' : '0',
          overflow: 'hidden',
          transition: 'height 1s ease-in-out',
        };
  if (isBuilderPage) {
    style.position = 'relative';
    style.height = 'auto';
  }
  return style;
};
